<template>
  <div class="exports-view">
    <BreadcrumbBar 
      :parentName="station?.name || 'Station'"
      currentName="Exports"
      :backRoute="station ? `/station/${station.slug}` : '/'"
    />

    <!-- Mini Player - Full Width -->
    <div v-if="playingExport" class="mini-player">
      <div class="mini-player-inner">
        <div class="player-info">
          <AppIcon name="file-audio" />
          <span class="now-playing-title">{{ playingExport.title || playingExport.filename }}</span>
        </div>
        <div class="player-controls">
          <button class="btn-player" @click="togglePlay">
            <AppIcon :name="isPlaying ? 'pause' : 'play'" />
          </button>
          <div class="progress-container">
            <div class="progress-track" @click="seek">
              <div class="progress-track-inner">
                <div class="progress-fill" :style="{ width: progressPercent + '%' }"></div>
              </div>
            </div>
            <span class="time-display">{{ currentTimeStr }} / {{ durationStr }}</span>
          </div>
          <input type="range" class="volume-slider" min="0" max="100" v-model="volume" @input="setVolume">
          <button class="btn-player" @click="stopPlayer">
            <AppIcon name="times" />
          </button>
        </div>
      </div>
      <audio ref="audio" @timeupdate="onTimeUpdate" @ended="onEnded" @loadedmetadata="onLoaded"></audio>
    </div>

    <div class="content">
      <!-- Toolbar -->
      <div class="content-toolbar">
        <div class="toolbar-left">
          <div class="search-box">
            <AppIcon name="search" />
            <input 
              type="text" 
              v-model="searchQuery" 
              placeholder="Search exports..." 
              @input="debouncedSearch"
            >
            <button v-if="searchQuery" class="btn-clear-search" @click="clearSearch">
              <AppIcon name="times" />
            </button>
          </div>
        </div>
        <div class="toolbar-right">
          <div class="date-filter">
            <label>From:</label>
            <input type="date" v-model="filterStartDate" @change="applyFilter">
            <label>To:</label>
            <input type="date" v-model="filterEndDate" @change="applyFilter">
            <button v-if="filterStartDate || filterEndDate" class="btn-clear" @click="clearDateFilter" title="Clear date filter">
              <AppIcon name="times" />
            </button>
          </div>
          <button v-if="voicesEnabled" class="btn-primary" style="padding: 8px 14px;" @click="goToVoices(null)">
            <AppIcon name="microphone" /> Voice Replication
          </button>
        </div>
      </div>

      <div v-if="loading" class="loading-state">
        <AppIcon name="spinner" spin />
        <p>Loading exports...</p>
      </div>

      <div v-else-if="error" class="error-state">
        <AppIcon name="exclamation-circle" />
        <p>{{ error }}</p>
        <button class="btn-primary" @click="fetchExports"><AppIcon name="redo" />Retry</button>
      </div>

      <div v-else-if="exports.length === 0" class="empty-state">
        <AppIcon name="folder-open" />
        <p>No exports found</p>
        <p class="hint" v-if="searchQuery || filterStartDate || filterEndDate">Try adjusting your search or date filter</p>
        <p class="hint" v-else>Create exports from the station editor by selecting a time range</p>
        <button class="btn-primary" @click="goToEditor"><AppIcon name="edit" />Go to Editor</button>
      </div>

      <div v-else class="exports-list">
        <table>
          <thead>
            <tr>
              <th>Title / Filename</th>
              <th>Duration</th>
              <th>Size</th>
              <th>Status</th>
              <th>Transcript</th>
              <th>Podcast</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="exp in exports" :key="exp.id" :class="{ 'playing-row': playingExport && playingExport.id === exp.id }">
              <td class="filename">
                <AppIcon name="file-audio" />
                <div class="title-info" v-if="editingExportId !== exp.id">
                  <div class="title-text">
                    <span v-if="exp.title" class="export-title">{{ exp.title }}</span>
                    <span :class="exp.title ? 'export-filename-small' : ''">{{ exp.filename || 'Processing...' }}</span>
                  </div>
                  <button class="btn-edit-title" @click="startEditTitle(exp)" title="Edit title">
                    <AppIcon name="pencil-alt" />
                  </button>
                </div>
                <div class="title-edit" v-else>
                  <input 
                    type="text" 
                    v-model="editingTitle" 
                    placeholder="Enter title..."
                    @keyup.enter="saveTitle(exp)"
                    @keyup.escape="cancelEditTitle"
                    ref="titleInput"
                  >
                  <button class="btn-save-title" @click="saveTitle(exp)" :disabled="savingTitle">
                    <AppIcon :name="savingTitle ? 'spinner' : 'check'" :spin="savingTitle && true" />
                  </button>
                  <button class="btn-cancel-title" @click="cancelEditTitle">
                    <AppIcon name="times" />
                  </button>
                </div>
              </td>
              <td>{{ formatDuration(exp.duration) }}</td>
              <td>{{ formatSize(exp.fileSize) }}</td>
              <td>
                <div v-if="exp.status === 'processing'" class="status-processing">
                  <div class="processing-progress">
                    <div class="processing-fill" :style="{ width: getExportProgress(exp.id) + '%' }"></div>
                  </div>
                  <span class="processing-text">{{ getExportProgress(exp.id) }}%</span>
                </div>
                <span v-else :class="['status-badge', exp.status]">
                  <i :class="statusIcon(exp.status)"></i> {{ exp.status }}
                </span>
              </td>
              <td class="transcript-cell">
                <!-- No transcript yet -->
                <template v-if="!exp.transcriptStatus && exp.status === 'complete'">
                  <button 
                    class="btn-transcribe" 
                    @click="requestTranscription(exp)"
                    :disabled="transcribing[exp.id]"
                    title="Transcribe audio"
                  >
                    <AppIcon :name="transcribing[exp.id] ? 'spinner' : 'microphone'" :spin="transcribing[exp.id] && true" />
                    Transcribe
                  </button>
                </template>
                <!-- Transcript in progress -->
                <template v-else-if="exp.transcriptStatus === 'pending' || exp.transcriptStatus === 'processing'">
                  <span :class="['transcript-status', getTranscriptStatusClass(exp.transcriptStatus)]">
                    <AppIcon name="spinner" spin />
                    {{ getTranscriptStatusText(exp.transcriptStatus) }}
                  </span>
                </template>
                <!-- Transcript completed -->
                <template v-else-if="exp.transcriptStatus === 'completed'">
                  <button class="btn-view-transcript" @click="viewTranscript(exp)" title="View transcript">
                    <AppIcon name="file-alt" />
                    View
                  </button>
                </template>
                <!-- Transcript failed -->
                <template v-else-if="exp.transcriptStatus === 'failed'">
                  <span class="transcript-status status-failed" :title="exp.transcriptError">
                    <AppIcon name="exclamation-circle" />
                    Failed
                  </span>
                  <button 
                    class="btn-retry-small" 
                    @click="requestTranscription(exp)"
                    :disabled="transcribing[exp.id]"
                    title="Retry transcription"
                  >
                    <AppIcon name="redo" />
                  </button>
                </template>
                <!-- Export not complete -->
                <template v-else>
                  <span class="transcript-status status-na">—</span>
                </template>
              </td>
              <!-- Podcast column -->
              <td class="podcast-cell">
                <!-- No transcript yet - can't generate -->
                <template v-if="exp.transcriptStatus !== 'completed'">
                  <span class="podcast-status status-na" title="Transcribe first">—</span>
                </template>
                <!-- Has transcript, no podcast yet -->
                <template v-else-if="!exp.podcast">
                  <button 
                    class="btn-generate-podcast" 
                    @click="handlePodcastGenerate(exp)"
                    title="Generate podcast metadata"
                  >
                    <AppIcon name="podcast" />
                    Generate
                  </button>
                </template>
                <!-- Has podcast metadata -->
                <template v-else>
                  <button class="btn-view-podcast" @click="viewPodcast(exp)" title="View podcast metadata">
                    <AppIcon name="podcast" />
                    View
                  </button>
                </template>
              </td>
              <td>{{ formatDate(exp.createdAt) }}</td>
              <td class="actions">
                <button 
                  v-if="exp.status === 'complete'" 
                  class="btn-action" 
                  @click="playExport(exp)"
                  :title="playingExport && playingExport.id === exp.id ? 'Playing' : 'Preview'"
                >
                  <AppIcon :name="playingExport && playingExport.id === exp.id && isPlaying ? 'pause' : 'play'" />
                </button>
                <button 
                  class="btn-action" 
                  @click="downloadExport(exp)"
                  title="Download"
                  :disabled="exp.status !== 'complete'"
                >
                  <AppIcon name="download" />
                </button>

                <button
                  v-if="exp.status === 'complete' && socialVideoEnabled"
                  class="btn-action btn-social-video"
                  @click.stop="isSocialVideoEligible(exp) && !svProcessing[exp.id] && goToSocialVideoEditor(exp)"
                  :disabled="svProcessing[exp.id] || !isSocialVideoEligible(exp)"
                  :title="svProcessing[exp.id] ? 'Processing video...' : (isSocialVideoEligible(exp) ? 'Generate Social Video' : 'Export must be 90s or under for social video')"
                >
                  <AppIcon :name="svProcessing[exp.id] ? 'spinner' : 'film'" :spin="svProcessing[exp.id] && true" />
                </button>
                <button 
                  class="btn-action btn-delete" 
                  @click="confirmDelete(exp)"
                  :title="isExportInProgress(exp) ? 'Cannot delete while in progress' : 'Delete'"
                  :disabled="isExportInProgress(exp)"
                >
                  <AppIcon name="trash" />
                </button>
              </td>
            </tr>
          </tbody>
        </table>

        <div class="pagination">
          <button 
            class="btn-page" 
            :disabled="!hasPrev" 
            @click="loadNewer"
            title="Newer exports"
          >
            <AppIcon name="chevron-left" />Newer
          </button>
          <span class="page-info">
            Showing {{ exports.length }} exports
            <span v-if="searchQuery || filterStartDate || filterEndDate"> (filtered)</span>
            <span v-if="totalStorage > 0" class="storage-total"> · Total storage: {{ formatSize(totalStorage) }}</span>
          </span>
          <button 
            class="btn-page" 
            :disabled="!hasMore" 
            @click="loadOlder"
            title="Older exports"
          >
            Older <AppIcon name="chevron-right" />
          </button>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div v-if="showDeleteModal" class="modal-overlay">
      <div class="modal">
        <div class="modal-header">
          <AppIcon name="exclamation-triangle" />
          <h3>Delete Export</h3>
        </div>
        <div class="modal-body">
          <p>Are you sure you want to delete this export?</p>
          <p class="export-name" v-if="exportToDelete">{{ exportToDelete.title || exportToDelete.filename }}</p>
          <p class="warning">This action cannot be undone.</p>
        </div>
        <div class="modal-actions">
          <button class="btn-cancel" @click="cancelDelete">Cancel</button>
          <button class="btn-confirm-delete" @click="deleteExport" :disabled="deleting">
            <AppIcon v-if="deleting" name="spinner" spin />
            <AppIcon v-else name="trash" />
            {{ deleting ? 'Deleting...' : 'Delete' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Transcript Modal -->
    <div v-if="showTranscriptModal && transcriptExport" class="modal-overlay">
      <div class="modal modal-transcript">
        <div class="modal-header">
          <div class="modal-title">
            <AppIcon name="file-alt" />
            <h3>Transcript</h3>
          </div>
          <button class="btn-close-modal" @click="closeTranscriptModal">
            <AppIcon name="times" />
          </button>
        </div>
        <div class="modal-subheader">
          <span class="transcript-export-name">{{ transcriptExport.title || transcriptExport.filename }}</span>
          <span v-if="transcriptExport.transcriptLanguage" class="transcript-language">
            <AppIcon name="globe" /> {{ transcriptExport.transcriptLanguage.toUpperCase() }}
          </span>
        </div>
        <div class="modal-body transcript-body">
          <textarea
            v-if="transcriptEditMode"
            v-model="transcriptEditText"
            class="transcript-edit-textarea"
            spellcheck="true"
          ></textarea>
          <div v-else class="transcript-text">{{ transcriptExport.transcriptText }}</div>
        </div>
        <div class="modal-actions">
          <template v-if="transcriptEditMode">
            <button class="btn-secondary" @click="cancelTranscriptEdit" :disabled="transcriptSaving">Cancel</button>
            <button class="btn-primary" @click="saveTranscriptEdit" :disabled="transcriptSaving">
              <AppIcon :name="transcriptSaving ? 'spinner' : 'check'" :spin="transcriptSaving" />{{ transcriptSaving ? 'Saving...' : 'Save' }}
            </button>
          </template>
          <template v-else>
            <button class="btn-secondary" @click="copyTranscript">
              <AppIcon name="copy" />Copy
            </button>
            <button class="btn-secondary" @click="startTranscriptEdit">
              <AppIcon name="edit" />Edit
            </button>
            <button class="btn-primary" @click="closeTranscriptModal">Close</button>
          </template>
        </div>
      </div>
    </div>

    <!-- Podcast Options Modal -->
    <div v-if="showPodcastOptionsModal && podcastExport" class="modal-overlay" @click.self="closePodcastOptionsModal">
      <div class="modal modal-podcast-options">
        <div class="modal-header">
          <div class="modal-title">
            <AppIcon name="podcast" />
            <h3>Generate Podcast Metadata</h3>
          </div>
          <button class="btn-close-modal" @click="closePodcastOptionsModal">
            <AppIcon name="times" />
          </button>
        </div>
        <div class="modal-body">
          <p class="modal-description">Select what metadata to generate from the transcript:</p>
          
          <div class="options-section">
            <h4>Metadata</h4>
            <label class="checkbox-option">
              <input type="checkbox" v-model="podcastOptions.title">
              <span class="checkbox-label">
                <strong>Title</strong>
                <span class="checkbox-hint">Episode title based on content</span>
              </span>
            </label>
            <label class="checkbox-option">
              <input type="checkbox" v-model="podcastOptions.description">
              <span class="checkbox-label">
                <strong>Description</strong>
                <span class="checkbox-hint">2-4 sentence summary</span>
              </span>
            </label>
            <label class="checkbox-option">
              <input type="checkbox" v-model="podcastOptions.chapters">
              <span class="checkbox-label">
                <strong>Chapters</strong>
                <span class="checkbox-hint">Topic segments with timestamps</span>
              </span>
            </label>
            <label class="checkbox-option">
              <input type="checkbox" v-model="podcastOptions.keywords">
              <span class="checkbox-label">
                <strong>Keywords</strong>
                <span class="checkbox-hint">Tags for discoverability</span>
              </span>
            </label>
          </div>

          <div class="options-section">
            <h4>Social Snippets</h4>
            <label class="checkbox-option">
              <input type="checkbox" v-model="podcastOptions.twitter">
              <span class="checkbox-label">
                <strong>Twitter/X</strong>
                <span class="checkbox-hint">Short post (~280 chars)</span>
              </span>
            </label>
            <label class="checkbox-option">
              <input type="checkbox" v-model="podcastOptions.facebook">
              <span class="checkbox-label">
                <strong>Facebook</strong>
                <span class="checkbox-hint">Longer post</span>
              </span>
            </label>
            <label class="checkbox-option">
              <input type="checkbox" v-model="podcastOptions.instagram">
              <span class="checkbox-label">
                <strong>Instagram</strong>
                <span class="checkbox-hint">Caption with emojis</span>
              </span>
            </label>
            <label class="checkbox-option">
              <input type="checkbox" v-model="podcastOptions.linkedin">
              <span class="checkbox-label">
                <strong>LinkedIn</strong>
                <span class="checkbox-hint">Professional tone</span>
              </span>
            </label>
          </div>
        </div>
        <div class="modal-actions">
          <button class="btn-secondary" @click="closePodcastOptionsModal">Cancel</button>
          <button 
            class="btn-primary" 
            @click="generatePodcast"
            :disabled="generatingPodcast || !hasSelectedPodcastOption"
          >
            <AppIcon v-if="generatingPodcast" name="spinner" spin />
            <AppIcon v-else name="magic" />
            {{ generatingPodcast ? 'Generating...' : 'Generate' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Podcast Preview Modal -->
    <div v-if="showPodcastPreviewModal && podcastExport" class="modal-overlay" @click.self="closePodcastPreviewModal">
      <div class="modal modal-podcast-preview">
        <div class="modal-header">
          <div class="modal-title">
            <AppIcon name="podcast" />
            <h3>Podcast Metadata</h3>
          </div>
          <button class="btn-close-modal" @click="closePodcastPreviewModal">
            <AppIcon name="times" />
          </button>
        </div>
        <div class="modal-body podcast-preview-body">
          <!-- Title -->
          <div v-if="podcastExport.podcast?.title" class="preview-section">
            <div class="preview-section-header">
              <h4>Title</h4>
              <button class="btn-edit-section" @click="startEditPodcastField('title', podcastExport.podcast.title)">
                <AppIcon name="pencil-alt" />
              </button>
            </div>
            <div v-if="editingPodcastField === 'title'" class="edit-field">
              <input type="text" v-model="editingPodcastValue" class="edit-input">
              <div class="edit-actions">
                <button class="btn-save-edit" @click="savePodcastField('title')" :disabled="savingPodcast">
                  <AppIcon :name="savingPodcast ? 'spinner' : 'check'" :spin="savingPodcast && true" />
                </button>
                <button class="btn-cancel-edit" @click="cancelEditPodcastField">
                  <AppIcon name="times" />
                </button>
              </div>
            </div>
            <p v-else class="preview-text title-text">{{ podcastExport.podcast.title }}</p>
          </div>

          <!-- Description -->
          <div v-if="podcastExport.podcast?.description" class="preview-section">
            <div class="preview-section-header">
              <h4>Description</h4>
              <div class="section-actions">
                <button class="btn-copy-section" @click="copyToClipboard(podcastExport.podcast.description)" title="Copy">
                  <AppIcon name="copy" />
                </button>
                <button class="btn-edit-section" @click="startEditPodcastField('description', podcastExport.podcast.description)">
                  <AppIcon name="pencil-alt" />
                </button>
              </div>
            </div>
            <div v-if="editingPodcastField === 'description'" class="edit-field">
              <textarea v-model="editingPodcastValue" class="edit-textarea" rows="4"></textarea>
              <div class="edit-actions">
                <button class="btn-save-edit" @click="savePodcastField('description')" :disabled="savingPodcast">
                  <AppIcon :name="savingPodcast ? 'spinner' : 'check'" :spin="savingPodcast && true" />
                </button>
                <button class="btn-cancel-edit" @click="cancelEditPodcastField">
                  <AppIcon name="times" />
                </button>
              </div>
            </div>
            <p v-else class="preview-text">{{ podcastExport.podcast.description }}</p>
          </div>

          <!-- Chapters -->
          <div v-if="podcastExport.podcast?.chapters?.length" class="preview-section">
            <div class="preview-section-header">
              <h4>Chapters</h4>
              <button class="btn-copy-section" @click="copyChapters" title="Copy">
                <AppIcon name="copy" />
              </button>
            </div>
            <div class="chapters-list">
              <div v-for="(chapter, idx) in podcastExport.podcast.chapters" :key="idx" class="chapter-item">
                <span class="chapter-time">{{ formatChapterTime(chapter.start) }}</span>
                <span class="chapter-title">{{ chapter.title }}</span>
              </div>
            </div>
          </div>

          <!-- Keywords -->
          <div v-if="podcastExport.podcast?.keywords?.length" class="preview-section">
            <div class="preview-section-header">
              <h4>Keywords</h4>
              <button class="btn-copy-section" @click="copyToClipboard(podcastExport.podcast.keywords.join(', '))" title="Copy">
                <AppIcon name="copy" />
              </button>
            </div>
            <div class="keywords-list">
              <span v-for="(keyword, idx) in podcastExport.podcast.keywords" :key="idx" class="keyword-tag">
                {{ keyword }}
              </span>
            </div>
          </div>

          <!-- Social Snippets -->
          <div v-if="podcastExport.podcast?.socialSnippets" class="preview-section">
            <h4>Social Snippets</h4>
            
            <div v-if="podcastExport.podcast.socialSnippets.twitter" class="social-snippet">
              <div class="snippet-header">
                <AppIcon name="twitter" />Twitter/X
                <button class="btn-copy-section" @click="copyToClipboard(podcastExport.podcast.socialSnippets.twitter)" title="Copy">
                  <AppIcon name="copy" />
                </button>
              </div>
              <p class="snippet-text">{{ podcastExport.podcast.socialSnippets.twitter }}</p>
            </div>

            <div v-if="podcastExport.podcast.socialSnippets.facebook" class="social-snippet">
              <div class="snippet-header">
                <AppIcon name="facebook" />Facebook
                <button class="btn-copy-section" @click="copyToClipboard(podcastExport.podcast.socialSnippets.facebook)" title="Copy">
                  <AppIcon name="copy" />
                </button>
              </div>
              <p class="snippet-text">{{ podcastExport.podcast.socialSnippets.facebook }}</p>
            </div>

            <div v-if="podcastExport.podcast.socialSnippets.instagram" class="social-snippet">
              <div class="snippet-header">
                <AppIcon name="instagram" />Instagram
                <button class="btn-copy-section" @click="copyToClipboard(podcastExport.podcast.socialSnippets.instagram)" title="Copy">
                  <AppIcon name="copy" />
                </button>
              </div>
              <p class="snippet-text">{{ podcastExport.podcast.socialSnippets.instagram }}</p>
            </div>

            <div v-if="podcastExport.podcast.socialSnippets.linkedin" class="social-snippet">
              <div class="snippet-header">
                <AppIcon name="linkedin" />LinkedIn
                <button class="btn-copy-section" @click="copyToClipboard(podcastExport.podcast.socialSnippets.linkedin)" title="Copy">
                  <AppIcon name="copy" />
                </button>
              </div>
              <p class="snippet-text">{{ podcastExport.podcast.socialSnippets.linkedin }}</p>
            </div>
          </div>
        </div>
        <div class="modal-actions">
          <button class="btn-secondary" @click="openPodcastOptions(podcastExport)">
            <AppIcon name="redo" />Regenerate
          </button>
          <button class="btn-primary" @click="closePodcastPreviewModal">Close</button>
        </div>
      </div>
    </div>

    <!-- Integration Required Modal -->
    <div v-if="showIntegrationModal" class="modal-overlay" @click.self="showIntegrationModal = false">
      <div class="modal integration-modal">
        <button class="btn-close-modal" @click="showIntegrationModal = false">
          <AppIcon name="times" />
        </button>
        <div class="modal-icon-wrapper">
          <div class="modal-icon warning">
            <AppIcon name="exclamation-triangle" />
          </div>
        </div>
        <h3>OpenAI Not Configured</h3>
        <p>To enable transcription, please configure your OpenAI API key in Settings.</p>
        <div class="modal-actions">
          <router-link v-if="isAdmin" to="/settings" class="btn-primary" @click="showIntegrationModal = false">
            Go to Settings
          </router-link>
          <button class="btn-secondary" @click="showIntegrationModal = false">OK</button>
        </div>
      </div>
    </div>

    <!-- Toast Notifications -->
    <div class="toast-container">
      <transition-group name="toast">
        <div 
          v-for="toast in toasts" 
          :key="toast.id" 
          :class="['toast', toast.type]"
        >
          <i :class="toastIcon(toast.type)"></i>
          <span>{{ toast.message }}</span>
        </div>
      </transition-group>
    </div>


  </div>
</template>

<script>
import { authFetch, updateTranscript, getSocialVideoStatus } from '../services/api'
import websocket from '../services/websocket'
import BreadcrumbBar from '../components/BreadcrumbBar.vue'

export default {
  name: 'ExportsView',
  components: {
    BreadcrumbBar
  },
  data() {
    return {
      station: null,
      exports: [],
      loading: true,
      error: null,
      hasMore: false,
      hasPrev: false,
      oldestDate: null,
      newestDate: null,
      totalStorage: 0,
      svProcessing: {}, // keyed by exportId — true while server is transcoding
      svPollTimers: {}, // keyed by exportId — poll interval refs
      totalExports: 0,
      filterStartDate: '',
      filterEndDate: '',
      searchQuery: '',
      searchTimeout: null,
      perPage: 20,
      // Feature flags
      transcriptionEnabled: false,
      podcastGenerationEnabled: false,
      socialVideoEnabled: true,
      voicesEnabled: true,
      // Player state
      playingExport: null,
      isPlaying: false,
      currentTime: 0,
      duration: 0,
      volume: 80,
      // Delete modal state
      showDeleteModal: false,
      exportToDelete: null,
      deleting: false,
      // Edit title state
      editingExportId: null,
      editingTitle: '',
      savingTitle: false,
      // Transcript modal state
      showTranscriptModal: false,
      transcriptExport: null,
      transcriptEditMode: false,
      transcriptEditText: '',
      transcriptSaving: false,
      transcribing: {},
      // Integration required modal
      showIntegrationModal: false,
      // Social video state
      svTitle: '',        // editable station name overlay
      svSubtitle: '',     // editable "NOW PLAYING" label
      svDescription: '',  // editable export title (multiline)
      svCaptionsEnabled: false, // dynamic subtitle captions from transcript
      svBaseHue: 272,        // base hue for all visualizer colors (0-360)
      svSaturation: 85,      // 0 = grayscale, 85 = vibrant color
      svLightness: 60,       // lightness for neutral colors (white/gray/black)
      svCustomLogoUrl: null, // client-side uploaded logo override (object URL)
      svBgPhotoUrl: null,   // backdrop template background photo (object URL)
      svCarouselOffset: 0,
      svCarouselPageSize: 4,
      svSwatches: [
        { hue: 272, name: 'Violet',  sat: 85, lit: 60 },
        { hue: 210, name: 'Blue',    sat: 85, lit: 60 },
        { hue: 185, name: 'Cyan',    sat: 85, lit: 60 },
        { hue: 155, name: 'Teal',    sat: 85, lit: 60 },
        { hue: 120, name: 'Green',   sat: 85, lit: 60 },
        { hue: 45,  name: 'Gold',    sat: 85, lit: 60 },
        { hue: 15,  name: 'Orange',  sat: 85, lit: 60 },
        { hue: 350, name: 'Red',     sat: 85, lit: 60 },
        { hue: 0,   name: 'White',   sat: 0,  lit: 92 },
        { hue: 0,   name: 'Gray',    sat: 0,  lit: 55 },
      ],
      // Podcast modal state
      showPodcastOptionsModal: false,
      showPodcastPreviewModal: false,
      podcastExport: null,
      generatingPodcast: false,
      podcastOptions: {
        title: true,
        description: true,
        chapters: true,
        keywords: true,
        twitter: true,
        facebook: false,
        instagram: true,
        linkedin: false
      },
      editingPodcastField: null,
      editingPodcastValue: '',
      savingPodcast: false,
      // WebSocket unsubscribe functions
      wsUnsubscribers: [],
      // Export progress tracking
      exportProgress: {},
      // Toast notifications
      toasts: [],
      toastId: 0
    }
  },
  watch: {
    // Redraw previews whenever idle state becomes visible — catches regenerate
    svState(newVal) {
      if (newVal === 'idle') {
        this.$nextTick(() => { this.drawAllPreviews(); this.drawTextPreview() })
      }
    },

  },
  computed: {
    slug() {
      return this.$route.params.slug
    },


    svCarouselCurrentPage() {
      return Math.floor(this.svCarouselOffset / this.svCarouselPageSize)
    },
    progressPercent() {
      return this.duration > 0 ? (this.currentTime / this.duration) * 100 : 0
    },
    currentTimeStr() {
      return this.formatTime(this.currentTime)
    },
    durationStr() {
      return this.formatTime(this.duration)
    },
    hasSelectedPodcastOption() {
      const o = this.podcastOptions
      return o.title || o.description || o.chapters || o.keywords || 
             o.twitter || o.facebook || o.instagram || o.linkedin
    },
    isAdmin() {
      return this.$store.getters['auth/isAdmin']
    }
  },
  async mounted() {
    await this.loadStation()
    await this.loadFeatures()
    await this.fetchExports()
    this.subscribeToWebSocket()
    this.checkSocialVideoStatuses()
  },
  // Re-check when navigating back from SocialVideoView (component already mounted)
  async activated() {
    this.checkSocialVideoStatuses()
  },
  async beforeRouteUpdate(to, from, next) {
    next()
    if (from.name === 'SocialVideo') {
      await this.fetchExports()
      this.checkSocialVideoStatuses()
    }
  },
  beforeUnmount() {
    this.stopPlayer()
    this.unsubscribeFromWebSocket()
    // Clear all social video poll timers
    Object.values(this.svPollTimers).forEach(t => clearInterval(t))
    this.svPollTimers = {}
    if (this.svAnimFrame) { cancelAnimationFrame(this.svAnimFrame); this.svAnimFrame = null }
    if (this.svMediaRecorder && this.svMediaRecorder.state !== 'inactive') {
      try { this.svMediaRecorder.stop() } catch (e) { /* ignore */ }
    }
  },
  methods: {
    viewPodcast(exp) {
      this.podcastExport = exp
      this.showPodcastOptionsModal = false
      this.showPodcastPreviewModal = true
    },
    closePodcastOptionsModal() {
      this.showPodcastOptionsModal = false
      this.podcastExport = null
    },
    closePodcastPreviewModal() {
      this.showPodcastPreviewModal = false
      this.podcastExport = null
      this.editingPodcastField = null
      this.editingPodcastValue = ''
    },
    goToSocialVideoEditor(exp) {
      this.$router.push(`/station/${this.slug}/exports/${exp.id}/social-video`)
    },
    goToVoices(exp) {
      const query = exp ? { exportId: exp.id, exportTitle: exp.title || exp.filename } : {}
      this.$router.push({ name: 'Voices', params: { slug: this.slug }, query })
    },
    isSocialVideoEligible(exp) {
      return exp.status === 'complete' && exp.duration <= 90 * 1000 + 500
    },
    // Check all eligible exports for in-progress social video transcoding
    async checkSocialVideoStatuses() {
      const eligible = this.exports.filter(e => this.isSocialVideoEligible(e))
      for (const exp of eligible) {
        try {
          const status = await getSocialVideoStatus(this.slug, exp.id)
          if (status.processing) {
            this.svProcessing = { ...this.svProcessing, [exp.id]: true }
            this.pollSocialVideoStatus(exp.id)
          }
        } catch (e) { /* no active job or request failed */ }
      }
    },
    pollSocialVideoStatus(exportId) {
      if (this.svPollTimers[exportId]) return // already polling
      const timer = setInterval(async () => {
        try {
          const status = await getSocialVideoStatus(this.slug, exportId)
          if (!status.processing) {
            this.svProcessing = { ...this.svProcessing, [exportId]: false }
            clearInterval(this.svPollTimers[exportId])
            delete this.svPollTimers[exportId]
          }
        } catch (e) {
          this.svProcessing = { ...this.svProcessing, [exportId]: false }
          clearInterval(this.svPollTimers[exportId])
          delete this.svPollTimers[exportId]
        }
      }, 4000)
      this.svPollTimers[exportId] = timer
    },
    async loadStation() {
      try {
        const res = await authFetch(`/api/stations/${this.slug}`)
        if (res.ok) {
          this.station = await res.json()
        }
      } catch (err) {
        console.error('Error loading station:', err)
      }
    },
    async loadFeatures() {
      try {
        const res = await authFetch('/api/features')
        if (res.ok) {
          const features = await res.json()
          this.transcriptionEnabled = features.transcription || false
          this.podcastGenerationEnabled = features.podcastGeneration || false
          this.socialVideoEnabled = features.socialVideo !== false
          this.voicesEnabled = features.voices !== false
        }
      } catch (err) {
        console.error('Error loading features:', err)
      }
    },
    async fetchExports({ before = null, after = null } = {}) {
      this.loading = true
      this.error = null
      try {
        let url = `/api/s/${this.slug}/exports?limit=${this.perPage}`
        
        if (before) {
          url += `&before=${encodeURIComponent(before)}`
        } else if (after) {
          url += `&after=${encodeURIComponent(after)}`
        }
        
        if (this.filterStartDate) {
          url += `&startDate=${encodeURIComponent(this.filterStartDate)}`
        }
        if (this.filterEndDate) {
          url += `&endDate=${encodeURIComponent(this.filterEndDate)}`
        }
        if (this.searchQuery.trim()) {
          url += `&search=${encodeURIComponent(this.searchQuery.trim())}`
        }
        
        const res = await authFetch(url)
        if (res.ok) {
          const data = await res.json()
          this.exports = data.exports || []
          this.hasMore = data.hasMore || false
          this.hasPrev = data.hasPrev || false
          this.oldestDate = data.oldestDate
          this.newestDate = data.newestDate
          this.totalStorage = data.totalStorage || 0
          this.totalExports = data.totalExports || 0
        } else {
          throw new Error('Failed to load exports')
        }
      } catch (err) {
        this.error = err.message
      } finally {
        this.loading = false
      }
    },
    debouncedSearch() {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout)
      }
      this.searchTimeout = setTimeout(() => {
        this.fetchExports()
      }, 300)
    },
    clearSearch() {
      this.searchQuery = ''
      this.fetchExports()
    },
    loadOlder() {
      if (this.oldestDate) {
        this.fetchExports({ before: this.oldestDate })
      }
    },
    loadNewer() {
      if (this.newestDate) {
        this.fetchExports({ after: this.newestDate })
      }
    },
    applyFilter() {
      this.fetchExports()
    },
    clearDateFilter() {
      this.filterStartDate = ''
      this.filterEndDate = ''
      this.fetchExports()
    },
    getExportDownloadUrl(exp) {
      const token = this.$store.getters['auth/accessToken']
      const base = `/api/s/${this.slug}/export/${exp.id}/download`
      return token ? `${base}?token=${encodeURIComponent(token)}` : base
    },
    downloadExport(exp) {
      window.open(this.getExportDownloadUrl(exp), '_blank')
    },
    // Player methods
    playExport(exp) {
      if (this.playingExport && this.playingExport.id === exp.id) {
        this.togglePlay()
        return
      }
      
      this.playingExport = exp
      this.currentTime = 0
      this.duration = 0
      this.isPlaying = false
      
      this.$nextTick(() => {
        const audio = this.$refs.audio
        if (audio) {
          audio.src = this.getExportDownloadUrl(exp)
          audio.volume = this.volume / 100
          audio.load()
          audio.play().then(() => {
            this.isPlaying = true
          }).catch(err => {
            console.error('Play error:', err)
          })
        }
      })
    },
    togglePlay() {
      const audio = this.$refs.audio
      if (!audio) return
      
      if (this.isPlaying) {
        audio.pause()
        this.isPlaying = false
      } else {
        audio.play().then(() => {
          this.isPlaying = true
        })
      }
    },
    stopPlayer() {
      const audio = this.$refs.audio
      if (audio) {
        audio.pause()
        audio.src = ''
      }
      this.playingExport = null
      this.isPlaying = false
      this.currentTime = 0
      this.duration = 0
    },
    seek(event) {
      event.preventDefault()
      event.stopPropagation()
      
      const audio = this.$refs.audio
      if (!audio || !this.duration || !isFinite(this.duration)) {
        return
      }
      
      const track = event.currentTarget
      const rect = track.getBoundingClientRect()
      const clickX = event.clientX - rect.left
      const percent = Math.max(0, Math.min(1, clickX / rect.width))
      const newTime = percent * this.duration
      
      try {
        audio.currentTime = newTime
      } catch (err) {
        console.error('[Seek] Error:', err)
      }
    },
    setVolume() {
      const audio = this.$refs.audio
      if (audio) {
        audio.volume = this.volume / 100
      }
    },
    onTimeUpdate() {
      const audio = this.$refs.audio
      if (audio) {
        this.currentTime = audio.currentTime
      }
    },
    onLoaded() {
      const audio = this.$refs.audio
      if (audio) {
        this.duration = audio.duration
      }
    },
    onEnded() {
      this.isPlaying = false
      this.currentTime = 0
    },
    formatTime(seconds) {
      if (!seconds || isNaN(seconds)) return '0:00'
      const hours = Math.floor(seconds / 3600)
      const mins = Math.floor((seconds % 3600) / 60)
      const secs = Math.floor(seconds % 60)
      if (hours > 0) {
        return `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
      }
      return `${mins}:${secs.toString().padStart(2, '0')}`
    },
    statusIcon(status) {
      switch (status) {
        case 'complete': return 'fas fa-check-circle'
        case 'processing': return 'fas fa-spinner fa-spin'
        case 'queued': return 'fas fa-clock'
        case 'failed': return 'fas fa-times-circle'
        default: return 'fas fa-question-circle'
      }
    },
    isExportInProgress(exp) {
      return exp.status === 'processing' || exp.status === 'queued'
    },
    formatDuration(ms) {
      if (!ms) return '--'
      const secs = Math.floor(ms / 1000)
      const mins = Math.floor(secs / 60)
      const hrs = Math.floor(mins / 60)
      if (hrs > 0) {
        return `${hrs}h ${mins % 60}m`
      }
      return `${mins}m ${secs % 60}s`
    },
    formatSize(bytes) {
      if (!bytes) return '--'
      if (bytes < 1024) return bytes + ' B'
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
      return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
    },
    formatDate(date) {
      if (!date) return '--'
      return new Date(date).toLocaleString()
    },
    goToEditor() {
      this.$router.push(`/station/${this.slug}`)
    },
    // Delete methods
    confirmDelete(exp) {
      // Stop playing if this export is playing
      if (this.playingExport && this.playingExport.id === exp.id) {
        this.stopPlayer()
      }
      this.exportToDelete = exp
      this.showDeleteModal = true
    },
    cancelDelete() {
      this.showDeleteModal = false
      this.exportToDelete = null
    },
    async deleteExport() {
      if (!this.exportToDelete || this.deleting) return
      
      this.deleting = true
      try {
        const res = await authFetch(`/api/s/${this.slug}/export/${this.exportToDelete.id}`, {
          method: 'DELETE'
        })
        
        if (res.ok) {
          // Remove from local list
          this.exports = this.exports.filter(e => e.id !== this.exportToDelete.id)
          this.showDeleteModal = false
          this.exportToDelete = null
          this.showToast('Export deleted successfully', 'success')
        } else {
          const data = await res.json()
          this.showToast(data.error || 'Failed to delete export', 'error')
        }
      } catch (err) {
        this.showToast('Failed to delete export: ' + err.message, 'error')
      } finally {
        this.deleting = false
      }
    },
    // WebSocket subscription methods
    subscribeToWebSocket() {
      // First unsubscribe any existing listeners to prevent duplicates
      this.unsubscribeFromWebSocket()
      
      // Only connect if not already connected to any station
      // Don't reconnect if already connected - just subscribe to events
      if (!websocket.connected) {
        websocket.connect(this.$store, this.slug)
      }
      
      // Subscribe to export events
      this.wsUnsubscribers.push(
        websocket.on('export-started', this.handleExportStarted)
      )
      
      this.wsUnsubscribers.push(
        websocket.on('export-progress', this.handleExportProgress)
      )
      
      this.wsUnsubscribers.push(
        websocket.on('export-complete', this.handleExportComplete)
      )
      
      this.wsUnsubscribers.push(
        websocket.on('export-failed', this.handleExportFailed)
      )
      
      this.wsUnsubscribers.push(
        websocket.on('transcript-update', this.handleTranscriptUpdate)
      )
    },
    unsubscribeFromWebSocket() {
      // Unsubscribe from all events
      this.wsUnsubscribers.forEach(unsub => unsub())
      this.wsUnsubscribers = []
    },
    // WebSocket event handlers
    handleExportStarted(msg) {
      if (msg.export) {
        // Check if already in list to prevent duplicates
        const exists = this.exports.some(e => e.id === msg.export.id)
        if (!exists) {
          this.exports.unshift(msg.export)
          this.exportProgress[msg.export.id] = 0
        }
      }
      this.showToast('Export started! Processing...', 'info')
    },
    handleExportProgress(msg) {
      this.exportProgress[msg.exportId] = msg.progress
    },
    handleExportComplete(msg) {
      const idx = this.exports.findIndex(e => e.id === msg.exportId)
      if (idx >= 0) {
        this.exports[idx] = {
          ...this.exports[idx],
          status: 'complete',
          fileSize: msg.fileSize,
          filename: msg.filename
        }
      }
      delete this.exportProgress[msg.exportId]
      this.showToast('Export complete! Ready to download.', 'success')
    },
    handleExportFailed(msg) {
      const idx = this.exports.findIndex(e => e.id === msg.exportId)
      if (idx >= 0) {
        this.exports[idx] = {
          ...this.exports[idx],
          status: 'failed',
          error: msg.error
        }
      }
      delete this.exportProgress[msg.exportId]
      this.showToast('Export failed: ' + (msg.error || 'Unknown error'), 'error')
    },
    handleTranscriptUpdate(msg) {
      const idx = this.exports.findIndex(e => e.id === msg.exportId)
      if (idx >= 0) {
        this.exports[idx] = {
          ...this.exports[idx],
          transcriptStatus: msg.transcriptStatus,
          transcriptText: msg.transcriptText,
          transcriptSegments: msg.transcriptSegments,
          transcriptLanguage: msg.transcriptLanguage,
          transcriptError: msg.transcriptError,
          transcriptCompletedAt: msg.transcriptCompletedAt
        }
        
        // Clear transcribing state
        delete this.transcribing[msg.exportId]
        
        // Show toast notification
        if (msg.transcriptStatus === 'completed') {
          this.showToast('Transcription complete!', 'success')
        } else if (msg.transcriptStatus === 'failed') {
          this.showToast('Transcription failed: ' + (msg.transcriptError || 'Unknown error'), 'error')
        }
      }
    },
    // Transcription methods
    async requestTranscription(exp) {
      if (this.transcribing[exp.id]) return
      
      // Check if integration is configured first
      try {
        const statusRes = await authFetch('/api/integrations/openai/status')
        if (statusRes.ok) {
          const statusData = await statusRes.json()
          if (!statusData.configured) {
            this.showIntegrationModal = true
            return
          }
        }
      } catch (err) {
        // Continue anyway, backend will catch it
      }
      
      this.transcribing[exp.id] = true
      try {
        const res = await authFetch(`/api/s/${this.slug}/export/${exp.id}/transcribe`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({})
        })
        
        if (res.ok) {
          // Update local state
          const idx = this.exports.findIndex(e => e.id === exp.id)
          if (idx >= 0) {
            this.exports[idx] = {
              ...this.exports[idx],
              transcriptStatus: 'pending'
            }
          }
          this.showToast('Transcription queued...', 'info')
        } else {
          const err = await res.json()
          // Check if integration not configured
          if (err.code === 'INTEGRATION_NOT_CONFIGURED') {
            this.showIntegrationModal = true
          } else {
            this.showToast(err.error || 'Failed to start transcription', 'error')
          }
          delete this.transcribing[exp.id]
        }
      } catch (err) {
        console.error('Error requesting transcription:', err)
        this.showToast('Failed to start transcription', 'error')
        delete this.transcribing[exp.id]
      }
    },
    viewTranscript(exp) {
      this.transcriptExport = exp
      this.showTranscriptModal = true
    },
    closeTranscriptModal() {
      this.showTranscriptModal = false
      this.transcriptExport = null
      this.transcriptEditMode = false
      this.transcriptEditText = ''
      this.transcriptSaving = false
    },
    startTranscriptEdit() {
      this.transcriptEditText = this.transcriptExport.transcriptText || ''
      this.transcriptEditMode = true
    },
    cancelTranscriptEdit() {
      this.transcriptEditMode = false
      this.transcriptEditText = ''
    },
    async saveTranscriptEdit() {
      if (!this.transcriptEditText.trim()) return
      this.transcriptSaving = true
      try {
        const result = await updateTranscript(this.slug, this.transcriptExport.id, this.transcriptEditText)
        if (result.success) {
          // Update local export record so captions use new segments immediately
          this.transcriptExport = {
            ...this.transcriptExport,
            transcriptText: this.transcriptEditText,
            transcriptSegments: result.transcriptSegments
          }
          // Also update in the exports list
          const idx = this.exports.findIndex(e => e.id === this.transcriptExport.id)
          if (idx !== -1) {
            this.exports[idx] = {
              ...this.exports[idx],
              transcriptText: this.transcriptEditText,
              transcriptSegments: result.transcriptSegments
            }
          }
          this.transcriptEditMode = false
          this.transcriptEditText = ''
          this.showToast('Transcript saved', 'success')
        }
      } catch (err) {
        console.error('[TRANSCRIPT] Save error:', err)
        this.showToast('Failed to save transcript', 'error')
      } finally {
        this.transcriptSaving = false
      }
    },
    async copyTranscript() {
      if (!this.transcriptExport?.transcriptText) return
      
      try {
        await navigator.clipboard.writeText(this.transcriptExport.transcriptText)
        this.showToast('Transcript copied to clipboard', 'success')
      } catch (err) {
        console.error('Failed to copy transcript:', err)
        this.showToast('Failed to copy transcript', 'error')
      }
    },
    getTranscriptStatusText(status) {
      const statusMap = {
        pending: 'Queued',
        processing: 'Transcribing...',
        completed: 'Ready',
        failed: 'Failed'
      }
      return statusMap[status] || status
    },
    getTranscriptStatusClass(status) {
      return {
        pending: 'status-pending',
        processing: 'status-processing',
        completed: 'status-complete',
        failed: 'status-failed'
      }[status] || ''
    },
    // Podcast methods
    async handlePodcastGenerate(exp) {
      // Check if integration is configured first
      try {
        const res = await authFetch('/api/integrations/openai/status')
        if (res.ok) {
          const data = await res.json()
          if (data.configured) {
            // Integration is configured, open the options modal
            this.openPodcastOptions(exp)
          } else {
            // Not configured, show integration modal
            this.showIntegrationModal = true
          }
        } else {
          this.showIntegrationModal = true
        }
      } catch (err) {
        console.error('Error checking integration:', err)
        this.showIntegrationModal = true
      }
    },
    openPodcastOptions(exp) {
      this.podcastExport = exp
      this.showPodcastPreviewModal = false
      this.showPodcastOptionsModal = true
    },
    // ─── Social Video ───────────────────────────────────────────────────────
    // ─── Social Video ───────────────────────────────────────────────────────
    async generatePodcast() {
      if (this.generatingPodcast || !this.podcastExport) return
      
      this.generatingPodcast = true
      try {
        const res = await authFetch(`/api/s/${this.slug}/export/${this.podcastExport.id}/generate-podcast`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ generate: this.podcastOptions })
        })
        
        if (res.ok) {
          const data = await res.json()
          
          // Update local export
          const idx = this.exports.findIndex(e => e.id === this.podcastExport.id)
          if (idx >= 0) {
            this.exports[idx] = {
              ...this.exports[idx],
              podcast: data.podcast
            }
            this.podcastExport = this.exports[idx]
          }
          
          this.showPodcastOptionsModal = false
          this.showPodcastPreviewModal = true
          this.showToast('Podcast metadata generated!', 'success')
        } else {
          const err = await res.json()
          this.showToast(err.error || 'Failed to generate podcast metadata', 'error')
        }
      } catch (err) {
        console.error('Error generating podcast:', err)
        this.showToast('Failed to generate podcast metadata', 'error')
      } finally {
        this.generatingPodcast = false
      }
    },
    startEditPodcastField(field, value) {
      this.editingPodcastField = field
      this.editingPodcastValue = value
    },
    cancelEditPodcastField() {
      this.editingPodcastField = null
      this.editingPodcastValue = ''
    },
    async savePodcastField(field) {
      if (this.savingPodcast || !this.podcastExport) return
      
      this.savingPodcast = true
      try {
        const res = await authFetch(`/api/s/${this.slug}/export/${this.podcastExport.id}/podcast`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ [field]: this.editingPodcastValue })
        })
        
        if (res.ok) {
          const data = await res.json()
          
          // Update local export
          const idx = this.exports.findIndex(e => e.id === this.podcastExport.id)
          if (idx >= 0) {
            this.exports[idx] = {
              ...this.exports[idx],
              podcast: data.podcast
            }
            this.podcastExport = this.exports[idx]
          }
          
          this.editingPodcastField = null
          this.editingPodcastValue = ''
          this.showToast('Saved!', 'success')
        } else {
          const err = await res.json()
          this.showToast(err.error || 'Failed to save', 'error')
        }
      } catch (err) {
        console.error('Error saving podcast field:', err)
        this.showToast('Failed to save', 'error')
      } finally {
        this.savingPodcast = false
      }
    },
    formatChapterTime(seconds) {
      const h = Math.floor(seconds / 3600)
      const m = Math.floor((seconds % 3600) / 60)
      const s = Math.floor(seconds % 60)
      
      if (h > 0) {
        return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`
      }
      return `${m}:${s.toString().padStart(2, '0')}`
    },
    copyChapters() {
      if (!this.podcastExport?.podcast?.chapters) return
      
      const text = this.podcastExport.podcast.chapters
        .map(ch => `${this.formatChapterTime(ch.start)} - ${ch.title}`)
        .join('\n')
      
      this.copyToClipboard(text)
    },
    async copyToClipboard(text) {
      try {
        await navigator.clipboard.writeText(text)
        this.showToast('Copied to clipboard', 'success')
      } catch (err) {
        console.error('Failed to copy:', err)
        this.showToast('Failed to copy', 'error')
      }
    },
    // Title editing methods
    startEditTitle(exp) {
      this.editingExportId = exp.id
      this.editingTitle = exp.title || ''
    },
    cancelEditTitle() {
      this.editingExportId = null
      this.editingTitle = ''
    },
    async saveTitle(exp) {
      if (this.savingTitle) return
      
      this.savingTitle = true
      try {
        const res = await authFetch(`/api/s/${this.slug}/export/${exp.id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ title: this.editingTitle.trim() })
        })
        
        if (res.ok) {
          // Update local export
          const idx = this.exports.findIndex(e => e.id === exp.id)
          if (idx >= 0) {
            this.exports[idx] = {
              ...this.exports[idx],
              title: this.editingTitle.trim() || null
            }
          }
          this.editingExportId = null
          this.editingTitle = ''
        } else {
          const data = await res.json()
          this.showToast(data.error || 'Failed to update title', 'error')
        }
      } catch (err) {
        this.showToast('Failed to update title: ' + err.message, 'error')
      } finally {
        this.savingTitle = false
      }
    },
    getExportProgress(exportId) {
      return this.exportProgress[exportId] || 0
    },
    // Toast notification methods
    showToast(message, type = 'info', duration = 5000) {
      const id = ++this.toastId
      this.toasts.push({ id, message, type })
      setTimeout(() => {
        this.toasts = this.toasts.filter(t => t.id !== id)
      }, duration)
    },
    toastIcon(type) {
      switch (type) {
        case 'success': return 'fas fa-check-circle'
        case 'error': return 'fas fa-times-circle'
        case 'warning': return 'fas fa-exclamation-triangle'
        default: return 'fas fa-info-circle'
      }
    }
  }
}
</script>

<style scoped>
.exports-view {
  min-height: calc(100vh - 74px);
  background: var(--gray-50);
}

.content {
  padding: 32px;
  max-width: 1400px;
  margin: 0 auto;
}

.content-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.toolbar-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.toolbar-right {
  display: flex;
  align-items: center;
  gap: 16px;
}

.search-box {
  position: relative;
  display: flex;
  align-items: center;
}

.search-box > svg {
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--gray-400);
  pointer-events: none;
}

.search-box input {
  padding: 8px 32px 8px 32px;
  border: 1px solid var(--gray-200);
  border-radius: 4px;
  font-size: 13px;
  width: 200px;
}

.search-box input:focus {
  outline: none;
  border-color: var(--gray-400);
}

.btn-clear-search {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: var(--gray-400);
  cursor: pointer;
  padding: 4px;
  line-height: 1;
}

.btn-clear-search:hover {
  color: var(--gray-600);
}

.date-filter {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
}

.date-filter label {
  color: var(--gray-500);
}

.date-filter input[type="date"] {
  padding: 6px 10px;
  border: 1px solid var(--gray-200);
  border-radius: 4px;
  font-size: 13px;
  color: var(--gray-700);
}

.date-filter input[type="date"]:focus {
  outline: none;
  border-color: var(--gray-400);
}

.btn-clear {
  width: 28px;
  height: 28px;
  border: 1px solid var(--gray-200);
  border-radius: 4px;
  background: var(--white);
  color: var(--gray-500);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-clear:hover {
  background: var(--gray-50);
  color: var(--gray-700);
}

.btn-back {
  width: 32px;
  height: 32px;
  border: 1px solid var(--gray-200);
  border-radius: 4px;
  background: var(--white);
  color: var(--gray-600);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;
}

.btn-back:hover {
  background: var(--gray-50);
  border-color: var(--gray-300);
}

h1 {
  font-size: 15px;
  font-weight: 600;
  margin: 0;
}

h1 .separator {
  color: var(--gray-300);
  font-weight: 400;
  margin: 0 4px;
}

/* Mini Player */
.mini-player {
  background: var(--gray-900);
  color: var(--white);
  padding: 12px 32px;
}

.mini-player-inner {
  display: flex;
  align-items: center;
  gap: 20px;
  max-width: 1400px;
  margin: 0 auto;
}

.player-info {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 200px;
}

.player-info svg {
  color: var(--gray-400);
}

.now-playing-title {
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 180px;
}

.player-controls {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
}

.btn-player {
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 50%;
  background: var(--white);
  color: var(--gray-900);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
}

.btn-player:hover {
  background: var(--gray-100);
}

.progress-container {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 12px;
}

.progress-track {
  flex: 1;
  height: 20px;
  background: transparent;
  cursor: pointer;
  position: relative;
  display: flex;
  align-items: center;
}

.progress-track-inner {
  width: 100%;
  height: 6px;
  background: var(--gray-700);
  border-radius: 3px;
  overflow: hidden;
  pointer-events: none;
}

.progress-fill {
  height: 100%;
  background: var(--white);
  border-radius: 3px;
  transition: width 0.1s;
  pointer-events: none;
}

.time-display {
  font-size: 12px;
  color: var(--gray-400);
  white-space: nowrap;
  min-width: 80px;
}

.volume-slider {
  width: 80px;
  height: 4px;
  -webkit-appearance: none;
  background: var(--gray-700);
  border-radius: 2px;
  cursor: pointer;
}

.volume-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  width: 12px;
  height: 12px;
  background: var(--white);
  border-radius: 50%;
  cursor: pointer;
}

.exports-content {
  max-width: 1200px;
  margin: 0 auto;
  padding: 24px;
}

.loading-state,
.error-state,
.empty-state {
  background: var(--white);
  border-radius: 8px;
  padding: 48px;
  text-align: center;
  color: var(--gray-500);
}

.loading-state i,
.error-state i,
.empty-state svg {
  font-size: 48px;
  margin-bottom: 16px;
  color: var(--gray-300);
}

.error-state svg {
  color: #dc2626;
}

.empty-state .hint {
  font-size: 14px;
  margin: 8px 0 24px;
}

.empty-state .btn-primary {
  padding: 10px 18px;
  font-size: 13px;
}

.empty-state .btn-primary svg {
  font-size: 13px;
  margin-bottom: 0;
  color: inherit;
}

.btn-primary {
  background: var(--gray-900);
  color: var(--white);
  border: none;
  padding: 10px 20px;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.btn-primary:hover {
  background: var(--gray-800);
}

.exports-list {
  background: var(--white);
  border-radius: 8px;
  overflow: hidden;
}

table {
  width: 100%;
  border-collapse: collapse;
}

th, td {
  padding: 12px 16px;
  text-align: left;
  border-bottom: 1px solid var(--gray-100);
}

th {
  background: var(--gray-50);
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  color: var(--gray-500);
}

td {
  font-size: 14px;
}

.playing-row {
  background: var(--gray-50);
}

.filename {
  display: flex;
  align-items: center;
  gap: 12px;
  max-width: 300px;
}

.filename > i {
  color: var(--gray-400);
  flex-shrink: 0;
}

.title-info {
  display: flex;
  flex-direction: row;
  align-items: center;
  overflow: hidden;
}

.title-info .title-text {
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.title-info:hover .btn-edit-title {
  opacity: 1;
}

.btn-edit-title {
  background: none;
  border: none;
  color: var(--gray-400);
  cursor: pointer;
  padding: 4px;
  margin-left: 8px;
  opacity: 0;
  transition: opacity 0.15s;
  flex-shrink: 0;
}

.btn-edit-title:hover {
  color: var(--gray-600);
}

.title-edit {
  display: flex;
  align-items: center;
  gap: 6px;
}

.title-edit input {
  padding: 6px 10px;
  border: 1px solid var(--gray-300);
  border-radius: 4px;
  font-size: 13px;
  width: 180px;
}

.title-edit input:focus {
  outline: none;
  border-color: var(--gray-500);
}

.btn-save-title,
.btn-cancel-title {
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
}

.btn-save-title {
  background: var(--success);
  color: white;
}

.btn-save-title:hover:not(:disabled) {
  background: #059669;
}

.btn-save-title:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-cancel-title {
  background: var(--gray-200);
  color: var(--gray-600);
}

.btn-cancel-title:hover {
  background: var(--gray-300);
}

.export-title {
  font-weight: 600;
  color: var(--gray-900);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.export-filename-small {
  font-size: 12px;
  color: var(--gray-500);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
}

.status-badge.complete {
  background: #dcfce7;
  color: #166534;
}

.status-badge.queued {
  background: #e0e7ff;
  color: #3730a3;
}

.status-badge.processing {
  background: #fef3c7;
  color: #92400e;
}

.status-badge.failed {
  background: #fef2f2;
  color: #dc2626;
}

.status-processing {
  display: flex;
  align-items: center;
  gap: 10px;
}

.processing-progress {
  width: 80px;
  height: 6px;
  background: var(--gray-200);
  border-radius: 3px;
  overflow: hidden;
}

.processing-fill {
  height: 100%;
  background: #f59e0b;
  border-radius: 3px;
  transition: width 0.3s;
}

.processing-text {
  font-size: 12px;
  font-weight: 500;
  color: #92400e;
  min-width: 32px;
}

.actions {
  display: flex;
  gap: 8px;
}

.btn-action {
  width: 32px;
  height: 32px;
  background: none;
  border: 1px solid var(--gray-200);
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--gray-600);
}

.btn-action:hover {
  background: var(--gray-50);
  border-color: var(--gray-300);
}

.status-icon {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--gray-400);
}

.status-icon.error {
  color: #dc2626;
}

.pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  border-top: 1px solid var(--gray-100);
}

.btn-page {
  padding: 8px 16px;
  border: 1px solid var(--gray-200);
  border-radius: 4px;
  background: var(--white);
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: var(--gray-600);
}

.btn-page:hover:not(:disabled) {
  background: var(--gray-50);
  border-color: var(--gray-300);
}

.btn-page:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-info {
  font-size: 14px;
  color: var(--gray-500);
}

/* Delete button */
.btn-action.btn-delete:hover {
  background: #fef2f2;
  border-color: #fecaca;
  color: #dc2626;
}

/* Modal styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal {
  background: var(--white);
  border-radius: 12px;
  width: 100%;
  max-width: 420px;
  box-shadow: var(--shadow-lg);
  overflow: hidden;
}

.modal-header {
  padding: 20px 24px;
  display: flex;
  align-items: center;
  gap: 12px;
  border-bottom: 1px solid var(--gray-100);
}

.modal-header svg {
  font-size: 18px;
  color: var(--gray-400);
}

.modal-header h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: var(--gray-900);
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;
}

.modal-body {
  padding: 24px;
}

.modal-body p {
  margin: 0;
  color: var(--gray-600);
}

.modal-body .export-name {
  font-weight: 600;
  color: var(--gray-900);
  background: var(--gray-50);
  padding: 10px 14px;
  border-radius: 6px;
  word-break: break-all;
}

.modal-body .warning {
  font-size: 13px;
  color: #dc2626;
  margin-top: 16px;
  margin-bottom: 0;
}

.modal-actions {
  padding: 16px 24px;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  background: var(--gray-50);
}

.btn-cancel {
  padding: 10px 20px;
  border: 1px solid var(--gray-200);
  border-radius: 6px;
  background: var(--white);
  color: var(--gray-700);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
}

.btn-cancel:hover {
  background: var(--gray-50);
  border-color: var(--gray-300);
}

.btn-confirm-delete {
  padding: 10px 20px;
  border: none;
  border-radius: 6px;
  background: #dc2626;
  color: var(--white);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
}

.btn-confirm-delete:hover:not(:disabled) {
  background: #b91c1c;
}

.btn-confirm-delete:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

/* Toast Notifications */
.toast-container {
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 2000;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.toast {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 20px;
  border-radius: 8px;
  background: var(--white);
  box-shadow: var(--shadow-md);
  font-size: 14px;
  min-width: 280px;
  max-width: 400px;
}

.toast svg {
  font-size: 18px;
  flex-shrink: 0;
}

.toast.info {
  border-left: 4px solid #3b82f6;
}

.toast.info svg {
  color: #3b82f6;
}

.toast.success {
  border-left: 4px solid #16a34a;
}

.toast.success svg {
  color: #16a34a;
}

.toast.error {
  border-left: 4px solid #dc2626;
}

.toast.error svg {
  color: #dc2626;
}

.toast.warning {
  border-left: 4px solid #f59e0b;
}

.toast.warning svg {
  color: #f59e0b;
}

/* Toast animations */
.toast-enter-active,
.toast-leave-active {
  transition: all 0.3s ease;
}

.toast-enter,
.toast-leave-to {
  opacity: 0;
  transform: translateX(100px);
}

/* Disabled button style */
.btn-action:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* Transcript column styles */
.transcript-cell {
  white-space: nowrap;
}

.btn-transcribe {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 500;
  background: var(--gray-100);
  border: 1px solid var(--gray-200);
  border-radius: 4px;
  color: var(--gray-600);
  cursor: pointer;
  transition: all 0.15s;
}

.btn-transcribe:hover:not(:disabled) {
  background: var(--gray-200);
  color: var(--gray-700);
}

.btn-transcribe:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-view-transcript {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 500;
  background: var(--accent);
  border: none;
  border-radius: 4px;
  color: white;
  cursor: pointer;
  transition: all 0.15s;
}

.btn-view-transcript:hover {
  background: var(--accent-hover);
}

.transcript-status {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  padding: 4px 8px;
  border-radius: 4px;
}

.transcript-status.status-pending,
.transcript-status.status-processing {
  color: var(--accent);
}

.transcript-status.status-failed {
  color: #DC2626;
  background: #FEE2E2;
}

.transcript-status.status-na {
  color: var(--gray-400);
}

.btn-retry-small {
  margin-left: 6px;
  padding: 4px 8px;
  font-size: 11px;
  background: none;
  border: 1px solid var(--gray-200);
  border-radius: 4px;
  color: var(--gray-500);
  cursor: pointer;
}

.btn-retry-small:hover:not(:disabled) {
  background: var(--gray-100);
  color: var(--gray-700);
}

/* Transcript Modal */
.modal-transcript {
  max-width: 700px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
}

.modal-transcript .modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid var(--gray-200);
}

.modal-transcript .modal-title {
  display: flex;
  align-items: center;
  gap: 10px;
}

.modal-transcript .modal-title i {
  color: var(--accent);
}

.modal-transcript .modal-title h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
}

.btn-close-modal {
  background: none;
  border: none;
  font-size: 18px;
  color: var(--gray-400);
  cursor: pointer;
  padding: 4px;
}

.btn-close-modal:hover {
  color: var(--gray-600);
}

.modal-subheader {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 20px;
  background: var(--gray-50);
  border-bottom: 1px solid var(--gray-200);
}

.transcript-export-name {
  font-size: 13px;
  font-weight: 500;
  color: var(--gray-700);
}

.transcript-language {
  font-size: 11px;
  color: var(--gray-500);
  display: flex;
  align-items: center;
  gap: 4px;
}

.transcript-body {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  max-height: 400px;
}

.transcript-text {
  font-size: 14px;
  line-height: 1.7;
  color: var(--gray-700);
  white-space: pre-wrap;
}

.transcript-edit-textarea {
  width: 100%;
  min-height: 320px;
  font-size: 14px;
  line-height: 1.7;
  color: var(--gray-700);
  background: var(--gray-50);
  border: 1px solid var(--gray-200);
  border-radius: 6px;
  padding: 12px;
  resize: vertical;
  font-family: var(--font-family);
  outline: none;
  transition: border-color 0.15s;
}

.transcript-edit-textarea:focus {
  border-color: var(--accent);
  box-shadow: 0 0 0 3px rgba(51, 147, 255, 0.1);
}

.modal-transcript .modal-actions {
  border-top: 1px solid var(--gray-200);
  padding: 16px 20px;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.btn-secondary {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  font-size: 13px;
  font-weight: 500;
  background: white;
  border: 1px solid var(--gray-200);
  border-radius: 4px;
  color: var(--gray-600);
  cursor: pointer;
}

.btn-secondary:hover {
  background: var(--gray-50);
}

.btn-primary {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  font-size: 13px;
  font-weight: 500;
  background: var(--accent);
  border: none;
  border-radius: 4px;
  color: white;
  cursor: pointer;
}

.btn-primary:hover {
  background: var(--accent-hover);
}

/* Podcast column styles */
.podcast-cell {
  white-space: nowrap;
}

.podcast-status.status-na {
  color: var(--gray-400);
}

.btn-generate-podcast {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 500;
  background: linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%);
  border: none;
  border-radius: 4px;
  color: white;
  cursor: pointer;
  transition: all 0.15s;
}

.btn-generate-podcast:hover {
  background: linear-gradient(135deg, #7C3AED 0%, #6D28D9 100%);
  transform: translateY(-1px);
}

.btn-view-podcast {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 500;
  background: var(--accent);
  border: none;
  border-radius: 4px;
  color: white;
  cursor: pointer;
  transition: all 0.15s;
}

.btn-view-podcast:hover {
  background: var(--accent-hover);
}

/* Podcast Options Modal */
.modal-podcast-options {
  max-width: 480px;
}

.modal-podcast-options .modal-header,
.modal-podcast-preview .modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid var(--gray-200);
}

.modal-podcast-options .modal-title,
.modal-podcast-preview .modal-title {
  display: flex;
  align-items: center;
  gap: 10px;
}

.modal-podcast-options .modal-title i,
.modal-podcast-preview .modal-title i {
  font-size: 20px;
  color: #8B5CF6;
}

.modal-podcast-options .modal-title h3,
.modal-podcast-preview .modal-title h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
}

.modal-podcast-options .modal-body {
  padding: 20px;
}

.modal-description {
  font-size: 14px;
  color: var(--gray-600);
  margin-bottom: 20px;
}

.options-section {
  margin-bottom: 24px;
}

.options-section:last-child {
  margin-bottom: 0;
}

.options-section h4 {
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  color: var(--gray-500);
  margin-bottom: 12px;
  letter-spacing: 0.5px;
}

.checkbox-option {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 12px;
  margin-bottom: 8px;
  background: var(--gray-50);
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.15s;
}

.checkbox-option:hover {
  background: var(--gray-100);
}

.checkbox-option input[type="checkbox"] {
  margin-top: 2px;
  width: 16px;
  height: 16px;
  cursor: pointer;
}

.checkbox-label {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.checkbox-label strong {
  font-size: 13px;
  font-weight: 500;
  color: var(--gray-700);
}

.checkbox-hint {
  font-size: 12px;
  color: var(--gray-500);
}

/* Podcast Preview Modal */
.modal-podcast-preview {
  max-width: 650px;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
}

.podcast-preview-body {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
}

.preview-section {
  margin-bottom: 24px;
  padding-bottom: 20px;
  border-bottom: 1px solid var(--gray-200);
}

.preview-section:last-child {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}

.preview-section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.preview-section-header h4 {
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  color: var(--gray-500);
  letter-spacing: 0.5px;
  margin: 0;
}

.section-actions {
  display: flex;
  gap: 6px;
}

.btn-edit-section,
.btn-copy-section {
  background: none;
  border: none;
  padding: 4px 8px;
  font-size: 12px;
  color: var(--gray-400);
  cursor: pointer;
  border-radius: 4px;
}

.btn-edit-section:hover,
.btn-copy-section:hover {
  background: var(--gray-100);
  color: var(--gray-600);
}

.preview-text {
  font-size: 14px;
  line-height: 1.6;
  color: var(--gray-700);
  margin: 0;
}

.preview-text.title-text {
  font-size: 16px;
  font-weight: 600;
  color: var(--gray-800);
}

/* Edit fields */
.edit-field {
  display: flex;
  gap: 8px;
  align-items: flex-start;
}

.edit-input {
  flex: 1;
  padding: 8px 12px;
  font-size: 14px;
  border: 1px solid var(--gray-300);
  border-radius: 4px;
}

.edit-textarea {
  flex: 1;
  padding: 8px 12px;
  font-size: 14px;
  border: 1px solid var(--gray-300);
  border-radius: 4px;
  resize: vertical;
  font-family: inherit;
}

.edit-actions {
  display: flex;
  gap: 4px;
}

.btn-save-edit,
.btn-cancel-edit {
  padding: 8px 10px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
}

.btn-save-edit {
  background: var(--accent);
  color: white;
}

.btn-save-edit:hover:not(:disabled) {
  background: var(--accent-hover);
}

.btn-cancel-edit {
  background: var(--gray-100);
  color: var(--gray-600);
}

.btn-cancel-edit:hover {
  background: var(--gray-200);
}

/* Chapters list */
.chapters-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.chapter-item {
  display: flex;
  gap: 12px;
  padding: 8px 12px;
  background: var(--gray-50);
  border-radius: 4px;
}

.chapter-time {
  font-family: monospace;
  font-size: 13px;
  color: var(--accent);
  min-width: 60px;
}

.chapter-title {
  font-size: 13px;
  color: var(--gray-700);
}

/* Keywords */
.keywords-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.keyword-tag {
  padding: 4px 10px;
  font-size: 12px;
  background: var(--gray-100);
  color: var(--gray-700);
  border-radius: 12px;
}

/* Social snippets */
.social-snippet {
  margin-top: 12px;
  padding: 12px;
  background: var(--gray-50);
  border-radius: 6px;
}

.snippet-header {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  font-weight: 600;
  color: var(--gray-600);
  margin-bottom: 8px;
}

.snippet-header i.fab {
  font-size: 14px;
}

.snippet-header .fa-twitter {
  color: #1DA1F2;
}

.snippet-header .fa-facebook {
  color: #4267B2;
}

.snippet-header .fa-instagram {
  color: #E4405F;
}

.snippet-header .fa-linkedin {
  color: #0077B5;
}

.snippet-header .btn-copy-section {
  margin-left: auto;
}

.snippet-text {
  font-size: 13px;
  line-height: 1.5;
  color: var(--gray-700);
  margin: 0;
  white-space: pre-wrap;
}

/* Integration Modal */
.integration-modal {
  max-width: 420px;
  text-align: center;
  padding: 40px 32px 32px;
  position: relative;
}

.integration-modal .btn-close-modal {
  position: absolute;
  top: 16px;
  right: 16px;
  background: none;
  border: none;
  font-size: 18px;
  color: var(--warning);
  cursor: pointer;
}

.integration-modal .btn-close-modal:hover {
  color: var(--gray-600);
}

.modal-icon-wrapper {
  display: flex;
  justify-content: center;
  margin-bottom: 24px;
}

.integration-modal .modal-icon {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.integration-modal .modal-icon.warning {
  background: #fef3c7;
}

.integration-modal .modal-icon.warning i {
  font-size: 32px;
  color: var(--warning);
}

.integration-modal h3 {
  font-size: 20px;
  font-weight: 600;
  color: var(--gray-900);
  margin: 0 0 16px 0;
}

.integration-modal p {
  font-size: 15px;
  color: var(--gray-500);
  margin: 0 0 28px 0;
  line-height: 1.5;
}

.modal-actions {
  display: flex;
  justify-content: center;
  gap: 12px;
}

.modal-actions .btn-primary,
.modal-actions .btn-secondary {
  padding: 10px 24px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.modal-actions .btn-primary {
  background: var(--accent);
  color: white;
  border: none;
}

.modal-actions .btn-primary:hover {
  background: var(--accent-hover);
}

.modal-actions .btn-secondary {
  background: var(--gray-100);
  color: var(--gray-700);
  border: 1px solid var(--gray-200);
}

.modal-actions .btn-secondary:hover {
  background: var(--gray-200);
}


.modal-actions .btn-outline {
  background: white;
  color: var(--gray-700);
  border: 1px solid var(--gray-300);
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.modal-actions .btn-outline:hover {
  background: var(--gray-50);
  border-color: var(--gray-400);
}
</style>